import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import OpportunityCard from "@/components/OpportunityCard";
import { CATEGORY_META } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bookmark, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATES = ["CA", "TX", "FL", "NY", "IL", "PA", "OH", "GA", "NC", "MI", "WA", "AZ", "NJ", "VA", "MA", "IN", "TN", "MO", "MD", "WI"];

export default function OpportunitiesListPage() {
  const { category } = useParams();
  const [params] = useSearchParams();
  const meta = CATEGORY_META[category];
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: "", min_sqft: "", max_price: "", min_score: "", q: params.get("q") || "" });
  const [sortBy, setSortBy] = useState("opportunity_score");

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    p.set("sort_by", sortBy);
    p.set("limit", "60");
    api.get(`/opportunities?${p.toString()}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  }, [category, filters, sortBy]);

  const saveSearch = async () => {
    const name = prompt("Name this search:");
    if (!name) return;
    try {
      await api.post("/saved-searches", { name, filters: { category, ...filters }, alert_enabled: false });
      toast.success("Search saved.");
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div className="space-y-5" data-testid="opps-list-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            {meta && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color, boxShadow: `0 0 8px ${meta.color}` }} />}
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold">{meta?.label || "All Opportunities"}</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            {loading ? "Loading…" : `${total.toLocaleString()} opportunities`}
          </h1>
        </div>
        <button data-testid="save-search-btn" onClick={saveSearch} className="flex items-center gap-2 text-[12px] px-3 py-2 rounded border border-[#33291f] text-[#b8ac96] hover:border-[#d4a574] hover:text-[#d4a574]">
          <Bookmark size={13} /> Save Search
        </button>
      </div>

      {/* Filters */}
      <div className="border border-[#33291f] rounded-lg p-4 bg-[#1c1712] grid grid-cols-2 md:grid-cols-6 gap-3">
        <Input placeholder="Search…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="filter-q" className="bg-[#241d16] border-[#33291f] col-span-2" />
        <Select value={filters.state || "all"} onValueChange={(v) => setFilters({ ...filters, state: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-state" className="bg-[#241d16] border-[#33291f]"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Min sqft" type="number" value={filters.min_sqft} onChange={(e) => setFilters({ ...filters, min_sqft: e.target.value })} data-testid="filter-min-sqft" className="bg-[#241d16] border-[#33291f]" />
        <Input placeholder="Max price ($)" type="number" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} data-testid="filter-max-price" className="bg-[#241d16] border-[#33291f]" />
        <Input placeholder="Min AI score" type="number" value={filters.min_score} onChange={(e) => setFilters({ ...filters, min_score: e.target.value })} data-testid="filter-min-score" className="bg-[#241d16] border-[#33291f]" />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[11px] text-[#7a6d5a]">Showing {items.length} of {total.toLocaleString()}</div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px] h-8 bg-[#1c1712] border-[#33291f] text-[12px]" data-testid="sort-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="opportunity_score">AI Score (High)</SelectItem>
            <SelectItem value="price">Price (High)</SelectItem>
            <SelectItem value="created_at">Newest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#7a6d5a]"><Loader2 className="animate-spin" size={20} /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((o) => <OpportunityCard key={o.id} opp={o} />)}
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="text-center py-16 text-[#7a6d5a] border border-dashed border-[#33291f] rounded-lg">No opportunities match the current filters.</div>
      )}
    </div>
  );
}
